<template>
    <div class="font-sans bg-[#f6f4f1] text-[#1f2937] min-h-screen py-10 px-4">
        <AppHeader />

        <div class="max-w-5xl mx-auto space-y-6">
            <h1 class="text-3xl font-bold">Meus Projetos</h1>

            <!-- LISTA DE PROJETOS -->
            <div v-for="proj in projects" :key="proj.id" class="bg-white shadow rounded-2xl p-6">
                <div class="flex justify-between items-start gap-4">
                    <!-- TÍTULO (ESQUERDA) -->
                    <div>
                        <router-link :to="{
                            name: 'detalhes',
                            params: { hash: proj.hash, slug: proj.slug }
                        }" class="block">
                            <h2 class="text-xl font-semibold text-[#1f2937] hover:underline cursor-pointer">
                                {{ proj.title }}
                            </h2>
                        </router-link>

                        <p class="text-gray-500 text-sm">
                            Criado em: {{ formatDate(proj.created_at) }}
                        </p>
                    </div>

                    <!-- BADGES (DIREITA) -->
                    <div class="flex flex-col items-end gap-2">
                        <!-- URGÊNCIA -->
                        <span :class="urgencyClass(proj.urgency_level)"
                            class="px-3 py-1 rounded-xl text-white text-sm font-semibold">
                            {{ urgencyLabel(proj.urgency_level) }}
                        </span>

                        <!-- STATUS -->
                        <span class="px-3 py-1 text-xs font-semibold rounded-full" :class="{
                            'bg-blue-100 text-blue-700': proj.status === 'open',
                            'bg-yellow-100 text-yellow-700': proj.status === 'in_progress',
                            'bg-green-100 text-green-700': proj.status === 'completed',
                            'bg-red-100 text-red-700': proj.status === 'cancelled',
                            'bg-orange-100 text-orange-700': proj.status === 'awaiting_approval',
                            'bg-purple-100 text-purple-700': proj.status === 'awaiting_payment',
                            'bg-emerald-100 text-emerald-700': proj.status === 'payment_approved',
                            'bg-amber-100 text-amber-700': proj.status === 'cancel_requested',
                            'bg-fuchsia-100 text-fuchsia-700': proj.status === 'dispute_opened'
                        }">
                            {{
                                proj.status === 'open' ? 'Aberto' :
                                    proj.status === 'in_progress' ? 'Em andamento' :
                                        proj.status === 'awaiting_payment' ? 'Aguardando pagamento' :
                                            proj.status === 'awaiting_approval' ? 'Aguardando aprovação' :
                                                proj.status === 'payment_approved' ? 'Pagamento aprovado' :
                                                    proj.status === 'completed' ? 'Concluído' :
                                                        proj.status === 'dispute_opened' ? 'Disputa aberta' :
                                                        proj.status === 'cancel_requested' ? 'Cancelamento solicitado' :
                                                        'Cancelado'
                            }}
                        </span>
                    </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-3 gap-4 my-4">
                    <div class="bg-gray-50 p-4 rounded-xl">
                        <p class="text-sm text-gray-500">Propostas</p>
                        <p class="text-xl font-semibold">{{ proj.proposals_count }}</p>
                    </div>
                    <div class="bg-gray-50 p-4 rounded-xl">
                        <p class="text-sm text-gray-500">Prazo Final</p>
                        <p class="text-xl font-semibold">{{ formatDate(proj.deadline) }}</p>
                    </div>
                    <div class="bg-gray-50 p-4 rounded-xl">
                        <p class="text-sm text-gray-500">Visualizações</p>
                        <p class="text-xl font-semibold">{{ proj.views_count }}</p>
                    </div>
                </div>

                <!-- BOTÃO ABRIR MODAL -->
                <div class="flex flex-wrap gap-3">
                    <button @click="openProposalsModal(proj)"
                        class="bg-[#b99a52] text-white px-5 py-2 rounded-xl font-semibold hover:bg-[#a18446] transition">
                        Ver Propostas
                    </button>

                    <button v-if="proj.status === 'awaiting_payment'" @click="goToPayment(proj)"
                        class="bg-emerald-600 text-white px-5 py-2 rounded-xl font-semibold hover:bg-emerald-700 transition">
                        Pagar
                    </button>
                </div>

            </div>
        </div>

        <div v-if="showProposalsModal"
            class="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-[2000]">
            <div class="bg-white w-full max-w-3xl p-6 rounded-2xl shadow-xl max-h-[85vh] overflow-y-auto">
                <h2 class="text-2xl font-bold mb-4">
                    Propostas — {{ currentProject.title }}
                </h2>

                <div v-for="proposal in currentProject.proposals" :key="proposal.id"
                    class="bg-gray-50 p-4 rounded-xl shadow mb-4">

                    <div class="flex justify-between items-start">
                        <div class="flex items-start gap-3">

                            <div class="flex flex-col">

                                <!-- NOME + ESTRELAS NA MESMA LINHA -->
                                <div class="flex items-center gap-2">
                                    <router-link :to="{
                                        name: 'freelancer',
                                        params: { hash: proposal.freelancer.hash, reference: proposal.freelancer.reference }
                                    }" class="font-semibold text-lg underline">
                                        {{ proposal.freelancer.name }}
                                    </router-link>

                                    <div class="flex items-center gap-1">
                                        <span v-for="n in 5" :key="n" class="text-lg" :class="n <= Math.round(proposal.freelancer.rating_average)
                                            ? 'text-yellow-500'
                                            : 'text-gray-300'">
                                            ★
                                        </span>
                                    </div>
                                </div>

                                <p class="text-gray-600 text-sm">Enviada em {{ formatDate(proposal.created_at) }}</p>

                            </div>

                            <!-- BADGE DE NOVAS MENSAGENS ** -->
                            <span v-if="unreadCount(proposal) > 0"
                                class="inline-flex items-center bg-red-600 text-white text-xs px-2.5 py-0.5 rounded-full font-semibold">
                                {{ unreadCount(proposal) === 1 ? '1 nova' : unreadCount(proposal) + ' novas' }}
                            </span>

                        </div>

                        <strong class="text-lg">
                            R$ {{ formatMoney(proposal.total_amount) }}
                        </strong>
                    </div>

                    <p class="mt-2 text-gray-700">
                        <strong>Dias estimados:</strong> {{ proposal.estimated_days }}
                    </p>

                    <button @click="openMessagesModal(proposal)"
                        class="mt-4 bg-gray-800 text-white px-4 py-2 rounded-xl">
                        Ver Conversa
                    </button>

                    <button v-if="proposal && proposal.status === 'pending'" @click="openAcceptModal(proposal)"
                        class="mt-4 ml-2 bg-green-600 text-white px-4 py-2 rounded-xl hover:bg-green-700">
                        Aceitar Proposta
                    </button>

                    <button
                        v-if="proposal && proposal.status === 'finished' && currentProject.status === 'awaiting_approval' && proposal.id === currentProject.accepted_proposal_id"
                        @click="openFinishModal(proposal)"
                        class="mt-4 ml-2 bg-blue-600 text-white px-4 py-2 rounded-xl hover:bg-blue-700">
                        Concluir Projeto
                    </button>

                    <button v-if="
                        proposal.status === 'finished' &&
                        currentProject.status === 'completed' &&
                        proposal.id === currentProject.accepted_proposal_id &&
                        currentProject.contractor_reviewed_freelancer === 0 &&
                        isReviewAvailable(currentProject.review_expires_at)
                    " @click="openReviewModal(currentProject, proposal)"
                        class="mt-4 ml-2 bg-yellow-500 text-white px-4 py-2 rounded-xl hover:bg-yellow-600">
                        Avaliar Freelancer
                    </button>

                </div>

                <button @click="closeProposalsModal" class="mt-4 bg-red-600 text-white px-4 py-2 rounded-xl w-full">
                    Fechar
                </button>
            </div>
        </div>

        <!-- ------------------------- -->
        <!-- MODAL DE MENSAGENS        -->
        <!-- ------------------------- -->
        <div v-if="showMessagesModal"
            class="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-[3000]">
            <div class="bg-white w-full max-w-2xl p-6 rounded-2xl shadow-xl max-h-[85vh] overflow-y-auto">
                <h2 class="text-xl font-bold mb-3">
                    Conversa — {{ selectedProposal.freelancer.name }}
                </h2>

                <div class="space-y-3">
                    <div v-for="msg in selectedProposal.messages" :key="msg.id" class="p-3 rounded-xl" :class="msg.sender_id === userData.user.id
                        ? 'bg-blue-100'
                        : 'bg-gray-200'
                        ">
                        <p class="text-sm text-gray-700 whitespace-pre-line">
                            {{ msg.content }}
                        </p>
                        <!-- EXIBIR ANEXOS -->
                        <div v-if="msg.attachments && msg.attachments.length" class="mt-2 space-y-1">
                            <div v-for="(file, idx) in msg.attachments" :key="idx">
                                <a :href="fileUrl(file)" target="_blank"
                                    class="text-blue-600 underline text-sm flex items-center gap-1">
                                    📎 {{ extractFileName(file) }}
                                </a>
                            </div>
                        </div>

                        <p class="text-xs text-gray-500 mt-1">
                            {{ formatDate(msg.created_at) }}
                        </p>
                    </div>
                </div>

                <div class="mt-4 flex flex-col gap-3">

                    <div v-if="selectedFiles.length" class="bg-gray-100 p-2 rounded-xl border text-sm">
                        <strong>Anexos selecionados:</strong>
                        <ul class="list-disc ml-5 mt-1">
                            <li v-for="(file, idx) in selectedFiles" :key="idx">
                                {{ file.name }}
                            </li>
                        </ul>
                    </div>

                    <div class="flex gap-2 items-center">
                        <textarea rows="3" v-model="newMessage" type="text" placeholder="Digite sua mensagem..."
                            class="input-gold" />

                        <!-- BOTÃO UPLOAD -->
                        <label class="cursor-pointer bg-gray-300 px-3 py-2 rounded-xl text-sm font-semibold">
                            📎
                            <input type="file" multiple class="hidden" @change="handleFileSelect" />
                        </label>

                        <button @click="sendMessage(selectedProposal)"
                            class="bg-[#b99a52] text-white px-4 py-2 rounded-xl font-semibold">
                            Enviar
                        </button>
                    </div>
                </div>

                <button @click="closeMessagesModal" class="mt-6 bg-red-600 text-white px-4 py-2 rounded-xl w-full">
                    Fechar Conversa
                </button>
            </div>
        </div>

        <!-- MODAL CONFIRMAR ACEITE -->
        <div v-if="showAcceptModal"
            class="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-[4000]">
            <div class="bg-white w-full max-w-lg p-6 rounded-2xl shadow-xl">

                <h2 class="text-2xl font-bold mb-4 text-center">
                    Confirmar Aceite da Proposta
                </h2>

                <p class="text-gray-700 mb-2">
                    <strong>Freelancer:</strong> {{ proposalToAccept.freelancer.name }}
                </p>

                <p class="text-gray-700 mb-2">
                    <strong>Valor da Proposta:</strong>
                    R$ {{ formatMoney(proposalToAccept.total_amount) }}
                </p>

                <p class="text-gray-700 mb-4">
                    <strong>Prazo Estimado:</strong>
                    {{ proposalToAccept.estimated_days }} dias
                </p>

                <p class="text-gray-800 text-center font-semibold mb-4">
                    Tem certeza que deseja aceitar esta proposta?
                </p>

                <div class="flex gap-3 mt-6">
                    <button @click="closeAcceptModal" class="flex-1 bg-gray-400 text-white px-4 py-2 rounded-xl">
                        Cancelar
                    </button>

                    <button @click="confirmAcceptProposal" :disabled="loadingAcceptProposal"
                        class="flex-1 bg-green-600 text-white px-4 py-2 rounded-xl hover:bg-green-700">
                        <span v-if="!loadingAcceptProposal">Confirmar</span>
                        <span v-else>Confirmando...</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- MODAL CONCLUIR PROJETO -->
        <div v-if="showFinishModal"
            class="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-[4000]">
            <div class="bg-white w-full max-w-lg p-6 rounded-2xl shadow-xl">

                <h2 class="text-2xl font-bold mb-4 text-center">
                    Confirmar Conclusão do Projeto
                </h2>

                <p class="text-gray-700 mb-2">
                    <strong>Freelancer:</strong> {{ proposalToFinish.freelancer.name }}
                </p>

                <p class="text-gray-700 mb-2">
                    <strong>Valor da Proposta:</strong>
                    R$ {{ formatMoney(proposalToFinish.total_amount) }}
                </p>

                <p class="text-gray-700 mb-4">
                    <strong>Prazo Estimado:</strong>
                    {{ proposalToFinish.estimated_days }} dias
                </p>

                <p class="text-gray-800 text-center font-semibold mb-4">
                    Tem certeza que deseja concluir o projeto?
                </p>

                <div class="flex gap-3 mt-6">
                    <button @click="closeFinishModal" class="flex-1 bg-gray-400 text-white px-4 py-2 rounded-xl">
                        Cancelar
                    </button>

                    <button @click="confirmFinishProposal" :disabled="loadingFinishProposal"
                        class="flex-1 bg-green-600 text-white px-4 py-2 rounded-xl hover:bg-green-700">
                        <span v-if="!loadingFinishProposal">Confirmar</span>
                        <span v-else>Confirmando...</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- MODAL AVALIAR CONTRATANTE -->
        <div v-if="showReviewModal"
            class="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-[5000]">
            <div class="bg-white w-full max-w-lg p-6 rounded-2xl shadow-xl">

                <!-- COMPONENTE DE AVALIAÇÃO -->
                <UserReview :projectId="projectToReview.id" :reviewedId="proposalToReview.freelancer_id"
                    :reviewerId="userData.user.id" review-type="freelancer"
                    :expires-at="projectToReview.review_expires_at" />

                <button @click="closeReviewModal" class="mt-4 bg-gray-400 text-white px-4 py-2 rounded-xl w-full">
                    Fechar
                </button>
            </div>
        </div>

        <AppFooter />
    </div>
</template>

<script setup>
import { useRouter } from 'vue-router'
import { ref, onMounted, getCurrentInstance } from 'vue'
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import UserReview from '@/components/UserReview.vue'
import api from '@/services/api'
import { useLoadingStore } from '@/store/loadingStore';

const router = useRouter()
const loadingStore = useLoadingStore();
const { proxy } = getCurrentInstance()

const projects = ref([])
const newMessage = ref('')
const userData = JSON.parse(localStorage.getItem('userData') || '{}')
const loadingAcceptProposal = ref(false)
const loadingFinishProposal = ref(false)
const selectedFiles = ref([])

/* MODAIS */
const showProposalsModal = ref(false)
const showMessagesModal = ref(false)
const currentProject = ref(null)
const selectedProposal = ref(null)
const showAcceptModal = ref(false)
const proposalToAccept = ref(null)
const showFinishModal = ref(false)
const proposalToFinish = ref(null)
const showReviewModal = ref(false)
const projectToReview = ref(null)
const proposalToReview = ref(null)

function isReviewAvailable(reviewExpiresAt) {
    if (!reviewExpiresAt) return false;

    const now = new Date();
    const expiresAt = new Date(reviewExpiresAt);

    return expiresAt.getTime() > now.getTime();
}

function handleFileSelect(event) {
    selectedFiles.value = Array.from(event.target.files)
}

function fileUrl(path) {
    if (!path) return '#'

    const normalized = path.replace(/\\/g, '/')
    return `${process.env.VUE_APP_URL_API_STORAGE}/${normalized}`
}

function extractFileName(path) {
    if (!path) return ''
    return path.split(/[/\\]/).pop()
}

/* ABRIR / FECHAR MODAL DE PROPOSTAS */
async function openProposalsModal(project) {
    loadingStore.show();

    try {
        const res = await api.get(`/proposal/project`, {
            params: {
                project_id: project.id,
            }
        })

        currentProject.value = {
            ...project,
            proposals: res.data.data || []
        };

        showProposalsModal.value = true;
    } catch (error) {
        proxy.$swal.fire({
            icon: "error",
            title: "",
            text: error?.response?.data?.error || "Erro ao carregar as propostas."
        })
    } finally {
        loadingStore.hide();
    }
}

function unreadCount(proposal) {

    return Number(proposal?.unread_messages) || 0;
}


function closeProposalsModal() {
    showProposalsModal.value = false
}

async function openMessagesModal(proposal) {
    selectedProposal.value = proposal
    showMessagesModal.value = true
    loadingStore.show();

    try {
        await loadConversations(proposal);

        try {
            await api.post('/messages/mark-read', {
                project_id: proposal.project_id,
                sender_id: proposal.freelancer_id
            });

            await api.post('/proposal/mark-read', {
                proposal_id: proposal.id
            });

            proposal.is_read = 1;
            proposal.unread_messages = 0;

            selectedProposal.value.messages = selectedProposal.value.messages.map(m => ({ ...m, is_read: 1 }));
        } catch (errMark) {
            console.warn('Não foi possível marcar como lido:', errMark?.response?.data || errMark.message || errMark);
            proxy.$swal.fire({
                icon: "error",
                title: "",
                text: error?.response?.data?.error || "Não foi possível marcar como lido"
            })
        }
    } catch (error) {
        proxy.$swal.fire({
            icon: "error",
            title: "",
            text: error?.response?.data?.error || "Erro ao carregar as mensagens.",

        })
    } finally {
        loadingStore.hide();
    }
}


function closeMessagesModal() {
    showMessagesModal.value = false
}

async function loadProjects() {
    try {
        loadingStore.show();
        const res = await api.get(`/projects/user/${userData.user.id}`)
        projects.value = res.data.data
    } catch (error) {
        proxy.$swal.fire({
            icon: "error",
            title: "",
            text: error?.response?.data?.error || "Erro ao buscar os projetos."
        })
    } finally {
        loadingStore.hide();
    }
}

async function loadConversations(proposal) {
    try {
        const res = await api.get(`/messages/conversation`, {
            params: {
                project_id: proposal.project_id,
                freelancer_id: proposal.freelancer_id
            }
        })
        console.log('teste');

        selectedProposal.value.messages = res.data.data || []
    } catch (error) {
        proxy.$swal.fire({
            icon: "error",
            title: "",
            text: error?.response?.data?.error || "Erro ao carregar as mensagens.",
            customClass: {
                container: 'swal-top-layer'
            }
        })
    }
}

async function sendMessage(proposal) {
    if (!newMessage.value.trim() && selectedFiles.value.length === 0) return

    try {
        loadingStore.show();

        const formData = new FormData();
        formData.append("project_id", proposal.project_id);
        formData.append("sender_id", userData.user.id);
        formData.append("receiver_id", proposal.freelancer_id);
        formData.append("content", newMessage.value);

        // ANEXOS
        selectedFiles.value.forEach((file, index) => {
            formData.append(`attachments[]`, file);
        });

        const res = await api.post("/messages/send", formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });

        // Adicionar no chat
        //selectedProposal.value.messages.push(res.data.data);
        await loadConversations(proposal);

        const payload = {
            project_id: proposal.project_id,
            receiver_id: proposal.freelancer_id,
            content: newMessage.value
        }
        await api.post("/messages/send-mail-freelancer", payload);

        // Limpar campos
        newMessage.value = "";
        selectedFiles.value = [];

    } catch (error) {
        console.log(error.response)
        proxy.$swal.fire({
            icon: "error",
            title: "",
            text: error?.response?.data?.error || "Erro ao enviar mensagem.",
            customClass: {
                container: 'swal-top-layer'
            }
        })
    } finally {
        loadingStore.hide();
    }
}

async function confirmAcceptProposal() {
    try {
        loadingStore.show();
        loadingAcceptProposal.value = true;
        await api.post('/proposal/accept', {
            proposal_id: proposalToAccept.value.id,
            project_id: proposalToAccept.value.project_id
        })

        proxy.$swal.fire({
            icon: "success",
            title: "Proposta aceita!",
            text: "Agora o freelancer será notificado.",
        })

        showAcceptModal.value = false;
        showProposalsModal.value = false;

        // Atualiza a listagem de projetos
        await loadProjects()

    } catch (error) {
        proxy.$swal.fire({
            icon: "error",
            title: "",
            text: error?.response?.data?.error || "Erro ao aceitar a proposta.",
            customClass: {
                container: 'swal-top-layer'
            }
        })
    } finally {
        loadingStore.hide()
        loadingAcceptProposal.value = false;
    }
}

async function confirmFinishProposal() {
    try {
        loadingStore.show();
        loadingFinishProposal.value = true;
        await api.post('/proposal/finish', {
            proposal_id: proposalToFinish.value.id,
            project_id: proposalToFinish.value.project_id
        })

        proxy.$swal.fire({
            icon: "success",
            //title: "Proposta aceita!",
            text: "Concluído com sucesso.",
            customClass: {
                container: 'swal-top-layer'
            }
        })

        showFinishModal.value = false;
        showProposalsModal.value = false;

        // Atualiza a listagem de projetos
        await loadProjects()

    } catch (error) {
        proxy.$swal.fire({
            icon: "error",
            title: "",
            text: error?.response?.data?.error || "Erro ao concluir.",
            customClass: {
                container: 'swal-top-layer'
            }
        })
    } finally {
        loadingStore.hide()
        loadingFinishProposal.value = false;
    }
}

function openAcceptModal(proposal) {
    proposalToAccept.value = proposal
    showAcceptModal.value = true
}

function closeAcceptModal() {
    showAcceptModal.value = false
}

function openFinishModal(proposal) {
    proposalToFinish.value = proposal
    showFinishModal.value = true
}

function closeFinishModal() {
    showFinishModal.value = false
}

function openReviewModal(project, proposal) {
    projectToReview.value = project
    proposalToReview.value = proposal
    showReviewModal.value = true
}

function closeReviewModal() {
    showReviewModal.value = false
}


function formatDate(dt) {
    if (!dt) return '-'
    return new Date(dt).toLocaleDateString('pt-BR')
}

function formatMoney(val) {
    return Number(val).toFixed(2).replace('.', ',')
}

function urgencyLabel(level) {
    return {
        low: 'Baixa urgência',
        medium: 'Urgência média',
        high: 'Alta urgência'
    }[level]
}

function urgencyClass(level) {
    return {
        low: 'bg-green-600',
        medium: 'bg-yellow-500',
        high: 'bg-red-600'
    }[level]
}

function goToPayment(proj) {
    router.push({
        name: 'project-payment',
        params: { hash: proj.hash, slug: proj.slug }
    })
}

onMounted(loadProjects)
</script>

<style>
.swal-top-layer {
    z-index: 99999 !important;
}
</style>